import React, { useState, useEffect } from 'react';
import { FormInstance } from 'antd/es/form';
import { get, map, filter } from 'lodash';
import moment, { Moment } from 'moment';
import { BiMessageCheck } from 'react-icons/bi';
import {
  getTimeFromTimeSlice,
  ScheduleTableWrapper,
  toLocaleTimeStringLocal,
  getDateTimeFromSelectedTimeSlice,
} from 'components/mentoring/ClassScheduleModal';
import { useTimezone } from 'libs/hooks';
import { TAvailableSchedule, TSelectedTimeSlice } from 'modules/mentoring';
import { CDateFormat, CDayOfWeek } from 'constants/base.const';
import { CScheduleStatus } from 'constants/mentoring.const';
import { CUserType, CUserTypeColor } from 'constants/user.const';
import { EUserType } from 'enums/base.enum';
import { EScheduleStatus } from 'enums/mentoring.enum';
import { Table, Button, Form, Typography, Row, Col, Tag, Switch, message } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { FormWrapper } from 'GlobalStyles';
import { SecondaryColor } from 'GlobalStyles';

const { Text, Title } = Typography;
type TAvailableClassScheduleProps = {
  userType: EUserType;
  form: FormInstance;
};
//회원가입 시
const AvailableClassSchedule = ({ userType, form }: TAvailableClassScheduleProps) => {
  const [selectedTimeSlices, setSelectedTimeSlices] = useState<Array<TSelectedTimeSlice>>([]);
  const tableRows = [
    { time_slice: -2 },
    { time_slice: -1 },
    ...map(Array(48), (v, index) => {
      return { time_slice: index };
    }),
    { time_slice: 48 },
  ];
  const [getDateTime, timezone] = useTimezone();

  return (
    <FormWrapper style={{ maxWidth: '380px' }}>
      <Form
        name='available_class_schedule'
        form={form}
        layout='vertical'
        initialValues={{ activate_mentoring: false }}
        className='form-inline'
      >
        <Tag color={CUserTypeColor[userType]}>{CUserType[userType]}</Tag>
        <Title level={2} style={{ marginBottom: '20px' }}>
          <span style={{ color: `${CUserTypeColor[userType]}` }}>수업 가능 일정</span>
          을<br />
          설정해주세요
        </Title>
        <Text style={{ display: 'block', marginBottom: '10px' }} strong>
          수업 가능 일정
        </Text>
        <div style={{ color: `${SecondaryColor}`, fontSize: '12px' }}>
          *앞으로 일주일 동안 가능한 시간만 먼저 등록해주세요. 그 이후 가능 기간 설정은 '내 정보 관리'에서
          업데이트할 수 있습니다.
        </div>
        <Form.Item
          name='available_schedules'
          noStyle
          rules={[
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (getFieldValue('activate_mentoring') === true && selectedTimeSlices.length == 0) {
                  message.error('수업 가능 일정을 선택해주세요');

                  return Promise.reject('Required');
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <AvailableSchedule
            selectedTimeSlices={selectedTimeSlices}
            setSelectedTimeSlices={setSelectedTimeSlices}
            startDateOfWeek={moment()}
            tableRows={tableRows}
          />
        </Form.Item>
        <Form.Item className='form-item' noStyle={true}>
          <Row>
            <Form.Item name={'activate_mentoring'}>
              <Switch checkedChildren='ON' unCheckedChildren='OFF' />
            </Form.Item>
            <Form.Item>
              <Text style={{ fontSize: '14px' }}>튜터 등록 설정하기.</Text>
            </Form.Item>
          </Row>
        </Form.Item>
        <BiMessageCheck style={{ color: '#fbbc05' }} />
        <div style={{ color: '#000', fontSize: '12px' }}>
          • 가입과 함께 튜터 등록 활성화 상태로 설정하시려면, 아래 항목을 작성하신 후 스위치를 켜주세요.
          활성화를 하셔야 멘티가 수업을 신청할 수 있습니다.
        </div>
        <div style={{ color: '#000', fontSize: '12px', marginTop: '5px' }}>
          • 현재는 McKinsey, BCG, Bain 분들만 멘토로 모집하고 있습니다. 최대한 빠른 시일 내에 서비스를
          확대하고, 안내 드리겠습니다.
        </div>
        <Form.Item shouldUpdate={true} noStyle={true}>
          {() => (
            <div className='steps-action'>
              <Row gutter={[4, 0]}>
                <Col span={6}>
                  <Button htmlType='submit' className='btn-center'>
                    나중에하기
                  </Button>
                </Col>
                <Col span={18}>
                  <Button
                    type='primary'
                    htmlType='submit'
                    size='large'
                    onClick={() => {
                      form.setFields([
                        {
                          name: 'available_schedules',
                          value: map(selectedTimeSlices, (elem) => ({
                            datetime_at: getDateTimeFromSelectedTimeSlice(elem, timezone),
                            status: EScheduleStatus.Available,
                          })),
                        },
                      ]);
                    }}
                  >
                    완료
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </Form.Item>
      </Form>
    </FormWrapper>
  );
};

const AvailableScheduleWrapper = styled.div`
  .time-cell span {
    position: absolute;
    top: -16px;
    left: 0px;
  }
  .time-cell {
    border: none;
  }
  .ant-table-cell {
    padding: 0px;
    text-align: center;
    font-size: 12px;
    cursor: pointer;
    height: 32px;
    line-height: 32px;
    div {
      z-index: 100;
    }
  }
`;

type TAvailableScheduleProps = {
  selectedTimeSlices: Array<TSelectedTimeSlice>;
  setSelectedTimeSlices: React.Dispatch<React.SetStateAction<Array<TSelectedTimeSlice>>>;
  startDateOfWeek: Moment;
  tableRows: Array<TAvailableSchedule>;
  isChecked?: boolean;
  selectedTimeSlicesForMultipleWeeks?: any;
  unavailableSchedule?: any;
};

//멘토링 가능 일정 설정(멘토)
export function AvailableSchedule({
  selectedTimeSlices,
  setSelectedTimeSlices,
  startDateOfWeek,
  tableRows,
  isChecked,
  selectedTimeSlicesForMultipleWeeks,
  unavailableSchedule,
}: TAvailableScheduleProps) {
  const [getDateTime, timezone] = useTimezone();
  const today = getDateTime('');
  const startOfTheWeek = today.clone().day('Sunday'); //_d: Sun Sep 05 2021 09:00:00 GMT+0900 (한국 표준시)
  const endOfTheWeek = today
    .clone()
    .day('Sunday')
    .add(7, 'd'); //_d: Sun Sep 12 2021 09:00:00 GMT+0900 (한국 표준시)

  function handleClickCell(event: any) {
    const className = event.target.closest('div')?.getAttribute('class'); //null or reserved or ...
    const timeSlice = event.target.closest('div')?.getAttribute('data-row'); //data-row={get(row, 'time_slice')}  // 0 ~ 47
    const dateAt = event.target.closest('div')?.getAttribute('data-col'); // data-col={columnDate.format(CDateFormat)} //2021-09-04

    // tableRows => [{time_slice:-2}, {time_slice:-1}, ~, time_slice:48}]
    if (isChecked) {
      //isChecked===true 매주 반복하기 체크했을 경우
      //가능한 일정으로 체크된 셀의 경우
      if (
        className == EScheduleStatus.Available &&
        startOfTheWeek.format(CDateFormat) <= dateAt &&
        dateAt < endOfTheWeek.format(CDateFormat)
      ) {
        // 이번주 범위 내에서만 체크된거 체크 해제
        setSelectedTimeSlices(
          selectedTimeSlicesForMultipleWeeks(
            filter(selectedTimeSlices, (v) => {
              return v.time_slice != timeSlice || v.date_at != dateAt;
            }),
          ),
        );
      } else if (startOfTheWeek.format(CDateFormat) <= dateAt && dateAt < endOfTheWeek.format(CDateFormat)) {
        // 이번주 범위 내에서만 체크 가능하도록
        //className을 갖고 있지 않은 경우  ==> 'available', 'unavailable', 'pending', 'reserved'가 모두 아닌 경우 ==> 체크 진행
        setSelectedTimeSlices(
          selectedTimeSlicesForMultipleWeeks([
            ...selectedTimeSlices,
            { time_slice: timeSlice, date_at: dateAt },
          ]),
        );
      }
    } else {
      //isChecked===false 매주 반복하기 체크하지 않았을 경우
      //가능한 일정으로 체크된 셀의 경우
      if (className == EScheduleStatus.Available) {
        // 체크된거 체크 해제
        setSelectedTimeSlices(
          filter(selectedTimeSlices, (v) => {
            return v.time_slice != timeSlice || v.date_at != dateAt;
          }),
        );
      } else if (!className) {
        //className을 갖고 있지 않은 경우  ==> 'available', 'unavailable', 'pending', 'reserved'가 모두 아닌 경우 ==> 체크 진행
        setSelectedTimeSlices([
          ...selectedTimeSlices,
          {
            time_slice: timeSlice,
            date_at: dateAt,
          },
        ]);
      }
    }
  }

  useEffect(() => {
    if (isChecked) {
      const formattedAlreadyUnavailable: any = [];

      unavailableSchedule?.forEach((el: any) => {
        const keys = Object.keys(el);
        const YYYY_MM_DD_Arr = keys.filter((key: string) => key !== 'time_slice');
        YYYY_MM_DD_Arr.forEach((YYYY_MM_DD: string) => {
          const unavailableObj = {
            time_slice: el.time_slice,
            date_at: YYYY_MM_DD,
            status: el[YYYY_MM_DD],
          };
          formattedAlreadyUnavailable.push(unavailableObj);
        });
      });

      //셀 관련 div 태그 중에서 class = 'available'인 부분은 불가능 일정 관련 class 부여하거나(회색) class attribute 아얘삭제(흰색)
      map(document.querySelectorAll('div[data-row][data-col]'), (cell) => {
        if (cell.getAttribute('class') === EScheduleStatus.Available) {
          cell.removeAttribute('class');

          //불가능 일정이었을때 다시 선택했다가 취소 할 경우
          for (let i = 0; i < formattedAlreadyUnavailable.length; i++) {
            if (
              cell.getAttribute('data-row') === String(formattedAlreadyUnavailable[i].time_slice) &&
              cell.getAttribute('data-col') === formattedAlreadyUnavailable[i].date_at
            ) {
              cell.setAttribute('class', `${formattedAlreadyUnavailable[i].status}`);
            }
          }
        }
      });
    } else {
      //셀 관련 div 태그 중에서 class = 'available'인 부분은 class attribute 삭제
      map(document.querySelectorAll('div[data-row][data-col]'), (cell) => {
        if (cell.getAttribute('class') === EScheduleStatus.Available) {
          cell.removeAttribute('class');
        }
      });
    }

    //selectedTimeSlices에 해당하는 available 가능 일정들의 태그들에 class = 'available' attribute를 부여한다
    map(selectedTimeSlices, (cell) => {
      const row = get(cell, 'time_slice');
      const col = moment(get(cell, 'date_at')).format(CDateFormat);
      const targetCell = document.querySelector(`div[data-row="${row}"][data-col="${col}"]`);
      if (targetCell) {
        targetCell.setAttribute('class', EScheduleStatus.Available);
      }
    });
  }, [selectedTimeSlices, startDateOfWeek]);

  //열
  const columns = [
    //첫번째 열
    {
      //dataIndex ==> dataSource에서 key 값과 동일하면 그 데이터를 넣음
      //render : dataSource값을 활용하여 값을 넣고싶다면
      dataIndex: 'time_slice',
      width: 90,
      //render 자체를 주석걸고 보면 -2 ~48 까지가 나열된다. (render가 없을 경우 dataSource에서 key 값과 동일하면 그 데이터를 넣기 때문)
      render: (value: string, row: TAvailableSchedule) => {
        //row => {time_slice : -2 } ~  {time_slice : 48}
        if (get(row, 'time_slice') < 0) {
          // {time_slice : -2 } or {time_slice : -1 } 의 경우 값이 없도록
          return '';
        } else {
          return {
            children: (
              <span style={{ fontWeight: 'bold' }}>
                {/* getTimeFromTimeSlice(5,timezone) =>  time_slice 정수값에서 momentTZ.Moment 형태의 time을 리턴*/}
                {/* toLocaleTimeStringLocal(Moment) Moment 형태의 시간 ->  '오전 6시 30분' */}
                {toLocaleTimeStringLocal(getTimeFromTimeSlice(get(row, 'time_slice'), timezone))}
              </span>
            ),
            props: { rowSpan: 1, className: 'time-cell' },
          };
        }
      },
    },
    //두 번째 열부터는 연속적 7개의 요소들
    ...map(Array(7), (v, index) => {
      //startDateOfWeek (그 주의 시작 ==> 일요일 날짜 데이터) 에서 index 값만큼 날짜 데이터를
      const columnDate = startDateOfWeek.clone().add(index, 'd');

      return {
        title: (
          <div style={{ height: '50px', lineHeight: '20px', color: '#000' }}>
            {`${get(CDayOfWeek, columnDate.day())}`}
            <br />
            {columnDate.format('M/D')}
          </div>
        ),
        dataIndex: columnDate.format(CDateFormat), //ex) "2021-08-29"
        render: (dayStatus: EScheduleStatus, row: TAvailableSchedule) => {
          // dayStatus: 'available', 'unavailable', 'pending', 'reserved'
          return (
            <>
              {get(row, 'time_slice') < 0 || get(row, 'time_slice') >= 48 ? (
                <div className={EScheduleStatus.UnAvailable}></div> //'time_slice'이 -2, -1, 48 일 때는
              ) : (
                <div
                  onClick={handleClickCell}
                  data-row={get(row, 'time_slice')}
                  className={dayStatus} // className별로 스타일링 구별 ('available', 'unavailable', 'pending', 'reserved' 별로) classScheduleModal.tsx 에서 ScheduleTableWrapper의 styled component 를 export
                  data-col={columnDate.format(CDateFormat)}
                >
                  {/* dayStatus 있을 때만. 없을 때는 그냥 흰 셀이기 때문*/}
                  {dayStatus && dayStatus != EScheduleStatus.Available ? (
                    <span>{CScheduleStatus[dayStatus]}</span> //슬랏을 열 수 있는 상태가 아닐 경우 ("예약가능","예약대기", "예약마감", ""(unavailable일 때) 텍스트 표시)
                  ) : (
                    <CheckOutlined style={{ color: '#fff' }} /> //가능하다고 체크한 슬랏의 경우 하얀색 체크 표시
                  )}
                </div>
              )}
            </>
          );
        },
      };
    }),
  ];

  return (
    <div style={{ marginTop: '20px' }}>
      <AvailableScheduleWrapper>
        <ScheduleTableWrapper>
          <Table
            tableLayout={'fixed'}
            //열
            columns={columns}
            bordered={true} // false 가로줄 선, true 세로줄까지 선
            //각각 열의 하위 데이터들 ==> colums의 render 에서 row라는 파라미터로 들어가는 데이터
            dataSource={tableRows}
            scroll={{ y: 350, scrollToFirstRowOnChange: false }}
            pagination={false}
          />
        </ScheduleTableWrapper>
      </AvailableScheduleWrapper>
    </div>
  );
}

export default AvailableClassSchedule;
